// components/system/AnalysisConfigPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Settings, RefreshCw, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Table2 } from 'lucide-react';

const API_BASE = '/analytics/v2/system/analysis-config';

const DATASET_LABELS = {
    acquisition_targets:  'Acquisition Targets',
    indie_gems:           'Indie Gems',
    unreleased_games:     'Unreleased Games',
    competitive_analysis: 'Competitive Analysis'
};

const DATASET_KEYS = Object.keys(DATASET_LABELS);

function getErrorMessage(json, fallback) {
    return json?.details || json?.error || fallback;
}

function fmtDate(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatusBadge({ status }) {
    const styles = {
        active:    'bg-green-100 text-green-700',
        validated: 'bg-blue-100 text-blue-700',
        draft:     'bg-yellow-100 text-yellow-700',
        rejected:  'bg-red-100 text-red-700'
    };
    return (
        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
            {status}
        </span>
    );
}

function DiffView({ diff }) {
    const [expanded, setExpanded] = useState(true);
    if (!diff) return null;

    const { sections, summary } = diff;

    if (!summary?.hasChanges) {
        return (
            <div className="text-sm text-gray-500 bg-gray-50 rounded p-3 mt-3">
                No differences — identical to the currently active config.
            </div>
        );
    }

    const changeLines = [];
    for (const [section, entries] of Object.entries(sections || {})) {
        for (const e of entries) {
            if (e.added)          changeLines.push({ marker: '+', section, key: e.key, from: null,       to: e.newValue });
            else if (e.removed)   changeLines.push({ marker: '-', section, key: e.key, from: e.oldValue, to: null });
            else if (e.changed)   changeLines.push({ marker: '~', section, key: e.key, from: e.oldValue, to: e.newValue });
            else if (e.enabledChanged) changeLines.push({ marker: '~', section, key: e.key, from: `enabled=${e.oldEnabled}`, to: `enabled=${e.newEnabled}` });
        }
    }

    return (
        <div className="mt-3 border rounded bg-gray-50">
            <button
                onClick={() => setExpanded(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
                <span>
                    Diff vs active — {summary.changed} changed · {summary.added} added · {summary.removed} removed
                </span>
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {expanded && (
                <div className="px-3 pb-3 text-xs font-mono space-y-0.5 max-h-48 overflow-y-auto">
                    {changeLines.map((line, i) => (
                        <div key={i} className={
                            line.marker === '+' ? 'text-green-700' :
                            line.marker === '-' ? 'text-red-700' :
                            'text-blue-700'
                        }>
                            {line.marker} {line.section}.{line.key}
                            {line.marker === '~' && <span className="text-gray-400"> {String(line.from)} → </span>}
                            {line.to !== null && <span>{String(line.to)}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const AnalysisConfigPanel = ({ onNotify }) => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [importingDatasetKey, setImportingDatasetKey] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [activating, setActivating] = useState(false);
    const [error, setError] = useState(null);
    const [showImportMenu, setShowImportMenu] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/status`);
            const json = await res.json();
            if (res.ok) {
                setStatus(json);
            } else {
                console.error('Failed to fetch analysis config status', json);
            }
        } catch (e) {
            console.error('Failed to fetch analysis config status', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchStatus(); }, []);

    const handleImport = async (datasetKey) => {
        setError(null);
        setImportResult(null);
        setShowImportMenu(false);
        setImportingDatasetKey(datasetKey);
        try {
            const res = await fetch(`${API_BASE}/import`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ datasetKey })
            });
            const json = await res.json();
            if (res.ok) {
                setImportResult({ ...json, datasetKey });
                if (datasetKey === 'all') {
                    const validatedCount = (json.results || []).filter(result => result.status === 'validated').length;
                    const rejectedCount = (json.results || []).filter(result => result.status === 'rejected').length;
                    onNotify?.(
                        `Imported all configs — ${validatedCount} validated, ${rejectedCount} rejected`,
                        rejectedCount > 0 ? 'error' : 'success'
                    );
                } else {
                    const s = json.status;
                    const datasetLabel = DATASET_LABELS[datasetKey] || datasetKey;
                    onNotify?.(
                        s === 'validated' ? `${datasetLabel} config imported and validated (v${json.configVersionId})` :
                        s === 'rejected'  ? `${datasetLabel} config imported but failed validation — fix errors before activating` :
                        `${datasetLabel} config imported (status: ${s})`,
                        s === 'rejected' ? 'error' : 'success'
                    );
                }
                fetchStatus();
            } else {
                const message = getErrorMessage(json, 'Import failed');
                console.error('Import analysis config failed', { status: res.status, response: json });
                setError(message);
                onNotify?.('Import failed: ' + message, 'error');
            }
        } catch (e) {
            setError(e.message);
            onNotify?.('Import failed: ' + e.message, 'error');
        } finally {
            setImportingDatasetKey(null);
        }
    };

    const handleActivate = async (configVersionId) => {
        setError(null);
        setActivating(true);
        try {
            const res = await fetch(`${API_BASE}/activate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ configVersionId })
            });
            const json = await res.json();
            if (res.ok) {
                onNotify?.('Config version activated successfully', 'success');
                setImportResult(null);
                fetchStatus();
            } else {
                const message = getErrorMessage(json, 'Activation failed');
                console.error('Activate analysis config failed', { status: res.status, response: json });
                setError(message);
                onNotify?.('Activation failed: ' + message, 'error');
            }
        } catch (e) {
            setError(e.message);
            onNotify?.('Activation failed: ' + e.message, 'error');
        } finally {
            setActivating(false);
        }
    };

    const activatePending = async (dk) => {
        const pending = status?.datasets?.[dk]?.pendingVersion;
        if (pending) await handleActivate(pending.id);
    };

    return (
        <div className="bg-white border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <Settings className="w-5 h-5 text-indigo-500" />
                <span>Analysis Config</span>
            </h3>

            {status?.sheetUrl && (
                <a
                    href={status.sheetUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mb-4 inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                >
                    <Table2 className="w-4 h-4" />
                    <span>Open Google Sheet</span>
                </a>
            )}

            {/* Status table */}
            <div className="mb-5">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                            <th className="pb-1 font-medium">Dataset</th>
                            <th className="pb-1 font-medium">Active</th>
                            <th className="pb-1 font-medium">Pending</th>
                            <th className="pb-1"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {DATASET_KEYS.map(dk => {
                            const ds = status?.datasets?.[dk];
                            const active = ds?.activeVersion;
                            const pending = ds?.pendingVersion;
                            return (
                                <tr key={dk} className="border-b last:border-0">
                                    <td className="py-2 text-gray-700 font-medium text-xs">{DATASET_LABELS[dk]}</td>
                                    <td className="py-2 text-xs text-gray-600">
                                        {active
                                            ? <span title={`Activated by ${active.activatedBy} on ${fmtDate(active.activatedAt)}`}>
                                                {active.configVersion} <span className="text-gray-400">({fmtDate(active.activatedAt)})</span>
                                              </span>
                                            : <span className="text-gray-400">—</span>
                                        }
                                    </td>
                                    <td className="py-2 text-xs">
                                        {pending
                                            ? <span className="flex items-center space-x-1">
                                                <StatusBadge status={pending.status} />
                                                <span className="text-gray-500">{pending.configVersion}</span>
                                              </span>
                                            : <span className="text-gray-400">—</span>
                                        }
                                    </td>
                                    <td className="py-2 text-right">
                                        {pending?.status === 'validated' && (
                                            <button
                                                onClick={() => activatePending(dk)}
                                                disabled={activating}
                                                className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                Activate
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {loading && <div className="text-xs text-gray-400 mt-2">Loading...</div>}
            </div>

            {/* Import */}
            <div className="border-t pt-4 space-y-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Import from Google Sheet</p>

                {error && (
                    <div className="flex items-start space-x-2 text-xs text-red-600 bg-red-50 rounded p-2">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2 relative flex">
                        <button
                            onClick={() => handleImport('all')}
                            disabled={Boolean(importingDatasetKey)}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm rounded-l hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {importingDatasetKey === 'all'
                                ? <><RefreshCw className="w-4 h-4 animate-spin" /><span>Importing...</span></>
                                : <><Settings className="w-4 h-4" /><span>Import All from Google Sheet</span></>
                            }
                        </button>
                        <button
                            onClick={() => setShowImportMenu(v => !v)}
                            disabled={Boolean(importingDatasetKey)}
                            className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-r border-l border-indigo-500 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label="Choose a single dataset import"
                        >
                            <ChevronDown className={`w-4 h-4 transition-transform ${showImportMenu ? 'rotate-180' : ''}`} />
                        </button>

                        {showImportMenu && (
                            <div className="absolute right-0 top-full mt-2 w-64 bg-white border rounded-lg shadow-lg z-10 overflow-hidden">
                                {DATASET_KEYS
                                    .filter(dk => status?.datasets?.[dk]?.importSupported)
                                    .map(dk => (
                                        <button
                                            key={dk}
                                            onClick={() => handleImport(dk)}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                        >
                                            Import {DATASET_LABELS[dk]}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>

                {importResult && (
                    <div className={`rounded border p-3 text-sm ${
                        importResult.datasetKey === 'all' ? 'bg-blue-50 border-blue-200' :
                        importResult.status === 'validated' ? 'bg-green-50 border-green-200' :
                        importResult.status === 'rejected'  ? 'bg-red-50 border-red-200' :
                        'bg-yellow-50 border-yellow-200'
                    }`}>
                        <div className="flex items-center space-x-2 font-medium">
                            {importResult.datasetKey === 'all'
                                ? <Settings className="w-4 h-4 text-blue-600" />
                                : importResult.status === 'validated'
                                ? <CheckCircle className="w-4 h-4 text-green-600" />
                                : <AlertCircle className="w-4 h-4 text-red-600" />
                            }
                            <span>
                                {importResult.datasetKey === 'all'
                                    ? 'Imported all config sets'
                                    : <>{DATASET_LABELS[importResult.datasetKey]} —{' '}
                                        {importResult.status === 'validated' && `Validated — version ${importResult.configVersionId}`}
                                        {importResult.status === 'rejected'  && 'Rejected — fix errors before activating'}
                                        {importResult.status === 'draft'     && 'Draft — validation skipped'}
                                      </>
                                }
                            </span>
                        </div>

                        {importResult.datasetKey === 'all' && (
                            <div className="mt-2 space-y-1 text-xs">
                                {(importResult.results || []).map((result) => (
                                    <div key={result.datasetKey} className="flex items-center justify-between rounded bg-white/60 px-2 py-1">
                                        <span className="font-medium text-gray-700">{DATASET_LABELS[result.datasetKey]}</span>
                                        <span className={
                                            result.status === 'validated' ? 'text-green-700' :
                                            result.status === 'rejected' ? 'text-red-700' :
                                            'text-yellow-700'
                                        }>
                                            {result.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {importResult.datasetKey !== 'all' && importResult.validation?.messages?.filter(msg => msg.severity === 'error').length > 0 && (
                            <ul className="mt-2 space-y-1 text-xs text-red-700">
                                {importResult.validation.messages
                                    .filter(msg => msg.severity === 'error')
                                    .map((msg, i) => <li key={i}>• {msg.message}</li>)}
                            </ul>
                        )}

                        {importResult.datasetKey !== 'all' && importResult.diff && <DiffView diff={importResult.diff} />}

                        {importResult.datasetKey !== 'all' && importResult.status === 'validated' && (
                            <button
                                onClick={() => handleActivate(importResult.configVersionId)}
                                disabled={activating}
                                className="mt-3 w-full flex items-center justify-center space-x-2 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                            >
                                {activating
                                    ? <><RefreshCw className="w-4 h-4 animate-spin" /><span>Activating...</span></>
                                    : <><CheckCircle className="w-4 h-4" /><span>Activate This Version</span></>
                                }
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnalysisConfigPanel;
